import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import { RestockPushPreview } from './RestockPushPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const daysSince = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null);

function DemandRow({ row, onPush }) {
  const status = row.hidden ? 'Hidden' : row.sold_out ? 'Sold out' : 'Orderable';
  const days = row.sold_out ? daysSince(row.sold_out_since) : null;
  return (
    <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_70px_70px_60px_110px_auto] gap-2 items-center border-b border-[#2A0F3D] py-2 font-mono text-xs"
      data-testid={`demand-row-${row.product_id}-${row.region}`}>
      <div className="flex items-center gap-2 min-w-0">
        {row.image_url ? <img src={row.image_url} alt="" className="w-8 h-8 object-cover shrink-0" /> : <div className="w-8 h-8 bg-[#050003] border border-[#2A0F3D] shrink-0" />}
        <div className="min-w-0">
          <p className="text-[#39FF14] truncate">{row.name}</p>
          <p className="text-[#6B8E73] text-[10px] uppercase sm:hidden">{row.region} · {row.waiting} waiting · {row.taps_30d} taps/30d · stock {row.stock}</p>
        </div>
      </div>
      <p className="hidden sm:block text-white" data-testid={`demand-waiting-${row.product_id}`}>{row.waiting}</p>
      <p className="hidden sm:block text-[#A3C7A8]" data-testid={`demand-taps-${row.product_id}`}>{row.taps_30d}</p>
      <p className="hidden sm:block text-[#A3C7A8]">{row.stock}</p>
      <p className={`hidden sm:block ${row.sold_out || row.hidden ? 'text-[#FF3B30]' : 'text-[#39FF14]'}`}>
        {status}{days !== null ? ` · ${days}d` : ''}
      </p>
      {!row.sold_out && !row.hidden && row.waiting > 0 ? (
        <button type="button" onClick={() => onPush(row)} className="border border-[#39FF14] text-[#39FF14] px-2 py-1 uppercase text-[10px] hover:bg-[#39FF14]/10"
          data-testid={`demand-push-${row.product_id}`}>Notify</button>
      ) : <span />}
    </div>
  );
}

// Inventory: products ranked by customers waiting now, then sold-out taps in the last 30 days.
export function SoldOutDemand({ region, refreshKey, onChanged }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(true);
  const [target, setTarget] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API_URL}/api/restock/demand`, { withCredentials: true, params: region ? { region } : {} })
      .then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, [region]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!items) return null;
  return (
    <div className="bg-[#0C0612] border border-[#FF3B30]/40 p-3 mb-6" data-testid="sold-out-demand">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="sold-out-demand-toggle">
        <span className="flex items-center gap-2 text-[#FF3B30] font-mono text-xs uppercase tracking-wider">
          <Users className="w-4 h-4" /> Sold out demand · {region ? region.toUpperCase() : 'ALL'} · {items.reduce((a, r) => a + r.waiting, 0)} waiting
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-[#6B8E73]" /> : <ChevronDown className="w-4 h-4 text-[#6B8E73]" />}
      </button>
      {open && (items.length === 0 ? (
        <p className="font-mono text-[11px] text-[#6B8E73] mt-3" data-testid="sold-out-demand-empty">// No one is waiting and no sold-out taps in the last 30 days.</p>
      ) : (
        <div className="mt-3">
          <div className="hidden sm:grid grid-cols-[1fr_70px_70px_60px_110px_auto] gap-2 font-mono text-[10px] uppercase text-[#6B8E73] pb-1 border-b border-[#2A0F3D]">
            <span>Product</span><span>Waiting</span><span>Taps 30d</span><span>Stock</span><span>Status</span><span />
          </div>
          {items.map((r) => <DemandRow key={`${r.product_id}-${r.region}`} row={r} onPush={setTarget} />)}
        </div>
      ))}
      <RestockPushPreview target={target ? { productId: target.product_id, region: target.region } : null}
        onDone={(changed) => { setTarget(null); if (changed) { load(); onChanged?.(); } }} />
    </div>
  );
}
